import { useQuery, type QueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { Category, Pack, Product, PublicSettings, Subcategory } from "./types";

/** How often the visible page re-checks the catalog for Admin changes (products, prices, availability, categories). */
export const CATALOG_REFRESH_MS = 30_000;

/**
 * Keeps catalog data (every ["catalog", …] query) and cart prices (["quote", …]) in step with the Admin:
 * refetched when a page opens, when the tab/app comes back to the foreground, and every
 * CATALOG_REFRESH_MS while the page is visible (polling pauses in the background).
 * Mobile browsers can restore a page from the back/forward cache without any of these events,
 * so a restored page refetches too. Returns a cleanup function.
 */
export function keepCatalogFresh(client: QueryClient) {
  const live = { staleTime: 0, refetchInterval: CATALOG_REFRESH_MS, refetchOnWindowFocus: true } as const;
  client.setQueryDefaults(["catalog"], live);
  client.setQueryDefaults(["quote"], live);
  const onPageShow = (e: PageTransitionEvent) => {
    if (!e.persisted) return;
    void client.invalidateQueries({ queryKey: ["catalog"] });
    void client.invalidateQueries({ queryKey: ["quote"] });
  };
  window.addEventListener("pageshow", onPageShow);
  return () => window.removeEventListener("pageshow", onPageShow);
}

export function useHome() {
  return useQuery({
    queryKey: ["catalog", "home"],
    queryFn: () =>
      api<{ categories: Category[]; packs?: Pack[]; seasonal?: Product[]; featured: Product[]; popular: Product[]; settings: PublicSettings }>("/catalog/home"),
  });
}

/** A category with its products; same query (and cache) as the category page. */
export function useCategory(slug: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["catalog", "category", slug],
    queryFn: () =>
      api<{ category: Category; subcategories: Subcategory[]; products: Product[] }>(`/catalog/categories/${encodeURIComponent(slug!)}`),
    enabled: enabled && !!slug,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ["catalog", "categories"],
    queryFn: () => api<{ categories: Category[] }>("/catalog/categories").then((r) => r.categories),
  });
}
